import { useCallback, useEffect, useState } from "react";
import { useToast } from "../../hooks/use-toast";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/authContext";
import { PRODUCT_TYPES, PRODUCT_TYPE_MARGIN_LIMITS } from "../../lib/constants";
import type { ProviderType, ProviderMargin } from "../../lib/types";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

interface ProviderMarginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: ProviderType | null;
}

export const ProviderMarginDialog = ({
  open,
  onOpenChange,
  provider,
}: ProviderMarginDialogProps) => {
  const [margins, setMargins] = useState<ProviderMargin[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingMargin, setEditingMargin] = useState<ProviderMargin | null>(
    null,
  );
  const [newMargin, setNewMargin] = useState({
    product_category_id: "",
    margin: "",
  });
  const [editMargin, setEditMargin] = useState({
    margin: "",
  });
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchMargins = useCallback(async () => {
    if (!provider?.id) return;
    try {
      const result = await api.getProviderMargins(provider.id);
      if (result?.success) {
        setMargins(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch margins:", error);
    }
  }, [provider?.id]);

  useEffect(() => {
    if (open && provider) {
      fetchMargins();
    }
  }, [open, provider, fetchMargins]);

  const handleAddMargin = async () => {
    if (!provider?.id || !user?.id) return;

    if (!newMargin.product_category_id || !newMargin.margin) {
      toast({
        title: "Validation Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    const marginValue = parseFloat(newMargin.margin);
    const maxLimit =
      PRODUCT_TYPE_MARGIN_LIMITS[newMargin.product_category_id] || 100;

    if (marginValue > maxLimit) {
      toast({
        title: "Validation Error",
        description: `Margin cannot exceed ${maxLimit}% for ${newMargin.product_category_id} products`,
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await api.createProviderMargin({
        provider_id: provider.id,
        product_category_id: newMargin.product_category_id,
        margin: marginValue,
        updated_by: user.id,
      });

      if (result?.success) {
        toast({
          title: "Margin Added",
          description: "Product margin added successfully",
        });
        setNewMargin({ product_category_id: "", margin: "" });
        setIsAdding(false);
        fetchMargins();
      } else {
        toast({
          title: "Error",
          description: result?.error || "Failed to add margin",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to add margin:", error);
      toast({
        title: "Error",
        description: "Failed to add margin",
        variant: "destructive",
      });
    }
  };

  const handleUpdateMargin = async () => {
    if (!editingMargin || !user?.id) return;

    if (!editMargin.margin) {
      toast({
        title: "Validation Error",
        description: "Please enter a margin value",
        variant: "destructive",
      });
      return;
    }

    const marginValue = parseFloat(editMargin.margin);
    const maxLimit =
      PRODUCT_TYPE_MARGIN_LIMITS[editingMargin.product_category_id] || 100;

    if (marginValue > maxLimit) {
      toast({
        title: "Validation Error",
        description: `Margin cannot exceed ${maxLimit}% for ${editingMargin.product_category_id} products`,
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await api.updateProviderMargin(editingMargin.id, {
        margin: marginValue,
        updated_by: user.id,
      });

      if (result?.success) {
        toast({
          title: "Margin Updated",
          description: "Product margin updated successfully",
        });
        setEditingMargin(null);
        setEditMargin({ margin: "" });
        fetchMargins();
      } else {
        toast({
          title: "Error",
          description: result?.error || "Failed to update margin",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to update margin:", error);
      toast({
        title: "Error",
        description: "Failed to update margin",
        variant: "destructive",
      });
    }
  };

  const handleDeleteMargin = async (marginId: string) => {
    if (!window.confirm("Are you sure you want to delete this margin?")) return;

    try {
      const result = await api.deleteProviderMargin(marginId);

      if (result?.success) {
        toast({
          title: "Margin Deleted",
          description: "Product margin deleted successfully",
        });
        fetchMargins();
      } else {
        toast({
          title: "Error",
          description: result?.error || "Failed to delete margin",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Failed to delete margin:", error);
      toast({
        title: "Error",
        description: "Failed to delete margin",
        variant: "destructive",
      });
    }
  };

  const startEditing = (margin: ProviderMargin) => {
    setEditingMargin(margin);
    setEditMargin({ margin: margin.margin.toString() });
  };

  const cancelEditing = () => {
    setEditingMargin(null);
    setEditMargin({ margin: "" });
  };

  const getAvailableProductTypes = () => {
    const usedTypes = margins.map(m => m.product_category_id);
    return PRODUCT_TYPES.filter(type => !usedTypes.includes(type));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Product Margins - {provider?.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add New Margin</h3>
              <Button variant="outline" onClick={() => setIsAdding(!isAdding)}>
                {isAdding ? "Cancel" : "Add Margin"}
              </Button>
            </div>

            {isAdding && (
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="product-type">Product Type</Label>
                  <Select
                    value={newMargin.product_category_id}
                    onValueChange={value =>
                      setNewMargin(prev => ({
                        ...prev,
                        product_category_id: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select product type" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableProductTypes().map(type => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="margin" className="text-sm mb-2">
                    Margin (%)
                  </Label>
                  <Input
                    id="margin"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter margin percentage"
                    value={newMargin.margin}
                    onChange={e =>
                      setNewMargin(prev => ({
                        ...prev,
                        margin: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="col-span-2">
                  <Button onClick={handleAddMargin}>Add Margin</Button>
                </div>
              </div>
            )}
          </div>

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product Type</TableHead>
                  <TableHead>Margin (%)</TableHead>
                  <TableHead>Updated By</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {margins.map(margin => (
                  <TableRow key={margin.id}>
                    <TableCell>
                      <Badge variant="secondary">
                        {margin.product_category_id}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {editingMargin?.id === margin.id ? (
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editMargin.margin}
                          onChange={e =>
                            setEditMargin(prev => ({
                              ...prev,
                              margin: e.target.value,
                            }))
                          }
                          className="w-20"
                        />
                      ) : (
                        `${margin.margin}%`
                      )}
                    </TableCell>
                    <TableCell>{margin.updated_by || "System"}</TableCell>
                    <TableCell>
                      {new Date(margin.updated_at).toLocaleString("en-IN", {
                        timeZone: "Asia/Kolkata",
                      })}
                    </TableCell>
                    <TableCell>
                      {editingMargin?.id === margin.id ? (
                        <div className="flex space-x-2">
                          <Button size="sm" onClick={handleUpdateMargin}>
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEditing}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startEditing(margin)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteMargin(margin.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {margins.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-muted-foreground"
                    >
                      No margins configured for this provider
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
